import { AxiosError } from 'axios';
import { put, select, takeEvery } from 'redux-saga/effects';
import { FeatureFlag, isFeatureEnabled } from 'Utils/FeatureFlags';
import logger from 'Utils/Logger';
import { awsRum } from 'Utils/AwsRum';
import {
  clearWriteFailed,
  clearWriteStarted,
  clearWriteSucceeded,
  hydrateRequested,
  playSessionOpened,
  playthroughCompleted,
  pointEarned,
  progressHydrated,
  selectCurrentExerciseId,
  selectLastCompletedSummary,
  selectPlaySessionId,
  sessionCompleted,
  sessionStarted,
} from 'Reducers/exerciseProgressReducer';
import type { SessionSummary } from 'Phaser/ExerciseSessionMachine/exerciseSessionMachine.types';
import {
  closePlaySession,
  ExerciseClearRequest,
  getExerciseProgress,
  openPlaySession,
  postExerciseClear,
} from 'Containers/ExerciseSection/exerciseProgressApi';
import { exercisePlaythroughCompletedEvent, getPlatformInfo } from 'Actions/events';
import type { ScaleProgressCell } from 'Types/ExerciseProgressTypes';

/** The app-session analytics id, minted once at startup and parked on the event
 *  reducer. Threaded onto the play-session lifecycle, the clear, and the emitted
 *  events so a whole sitting joins on one analytics grain. */
type WithEventReducer = { eventReducer: { sessionId: string } };

/**
 * Progress wiring for the exercise section (MUS-318, 7c). The saga is the relay/
 * mirror half of "React reacts to engine events" (#115): the machine emits facts,
 * this saga observes them and talks to the backend. It never drives the machine.
 *
 * Dark by default: the HTTP surface it calls is MUS-458 (not live yet), so every
 * worker no-ops unless USE_EXERCISES is on. That guard is defense in depth on top
 * of the launcher already being flag-gated.
 */

const isServerErrorOrNonAxios = (err: unknown): boolean =>
  !(err instanceof AxiosError) || (err.response?.status ?? 0) > 499;

/**
 * `sessionStarted` -> open the backend play-session for this sitting (MUS-459). The
 * backend mints the row and emits session-started; we park the returned id so the
 * clear, the playthrough events, and the close all join on it.
 *
 * Degrade, never crash: a failed open retries once, then the sitting proceeds with no
 * play_session_id. Playthroughs and the clear still post (valid analytics_session_id,
 * null sitting join) and the two server emits still fire, since they key on the
 * analytics id, not the sitting id. Open is minted once per sitting and the retry is
 * keyed on not-yet-holding an id, so a lost-response retry cannot mint a second sitting.
 */
export function* openExercisePlaySession(
  action: ReturnType<typeof sessionStarted>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const { exerciseId } = action.payload;
  const { sessionId } = (yield select(
    (state: WithEventReducer) => state.eventReducer,
  )) as WithEventReducer['eventReducer'];

  const body = {
    exercise_id: exerciseId,
    analytics_session_id: sessionId,
    datetime: new Date().toISOString(),
    platform: getPlatformInfo(),
  };

  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const playSessionId = (yield openPlaySession(body)) as string;
      yield put(playSessionOpened({ playSessionId }));
      return;
    } catch (err) {
      logger.error('[exercise-progress] play-session open failed', err);
      if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
    }
  }
  logger.warn(
    '[exercise-progress] play-session open did not succeed; sitting proceeds without a play_session_id',
  );
}

/**
 * `playthroughCompleted` (the slice fact, emitted for every graded take) -> post an
 * ExercisePlaythroughCompletedEvent through the events pipeline (MUS-459). The pipeline
 * merges datetime / analytics_session_id / platform; we supply the take's shape plus the
 * sitting join. No-input takes never reach here (the machine emits no playthrough for
 * GRADE_NO_INPUT), so free takes are correctly never posted.
 */
export function* postPlaythroughEvent(
  action: ReturnType<typeof playthroughCompleted>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const exerciseId = (yield select(selectCurrentExerciseId)) as number | null;
  if (exerciseId == null) {
    logger.debug('[exercise-progress] playthrough with no active exercise; post skipped');
    return;
  }

  const playSessionId = (yield select(selectPlaySessionId)) as string | null;
  const { cell, config, accuracy, verdict, playthroughIndex } = action.payload;

  yield put(
    exercisePlaythroughCompletedEvent({
      exercise_id: exerciseId,
      scale: cell.scale,
      tonic: cell.tonic,
      hand: config.hand,
      range_octaves: config.range_octaves,
      direction: config.direction,
      rhythm_density: config.rhythm_density,
      tempo: config.tempo,
      accuracy,
      verdict,
      playthrough_index: playthroughIndex,
      play_session_id: playSessionId ?? undefined,
    }),
  );
}

/**
 * `pointEarned` -> relay an earned clear, then take the cell the server returns
 * (server-authoritative: the backend owns domination/prune). No optimistic insert,
 * so no rollback. The engine already counted the clear in-sitting (#128); a failed
 * write costs only the durable mirror, which the next hydrate reconciles.
 */
export function* relayEarnedClear(
  action: ReturnType<typeof pointEarned>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const exerciseId = (yield select(selectCurrentExerciseId)) as number | null;
  if (exerciseId == null) {
    logger.debug('[exercise-progress] pointEarned with no active exercise; write skipped');
    return;
  }

  const { cell, config, mastered } = action.payload;
  const { sessionId } = (yield select(
    (state: WithEventReducer) => state.eventReducer,
  )) as WithEventReducer['eventReducer'];
  const playSessionId = (yield select(selectPlaySessionId)) as string | null;
  yield put(clearWriteStarted());
  const body: ExerciseClearRequest = {
    exercise_id: exerciseId,
    scale: cell.scale,
    tonic: cell.tonic,
    hand: config.hand,
    range_octaves: config.range_octaves,
    direction: config.direction,
    rhythm_density: config.rhythm_density,
    tempo: config.tempo,
    mastered,
    // MUS-459: the sitting join (null when the open degraded) and the analytics grain
    // the backend stamps onto the point-earned + tier-completed emits.
    play_session_id: playSessionId,
    analytics_session_id: sessionId,
  };

  try {
    const serverCell = (yield postExerciseClear(body)) as ScaleProgressCell;
    yield put(clearWriteSucceeded({ exerciseId, cell: serverCell }));
  } catch (err) {
    logger.error('[exercise-progress] earned-clear write failed', err);
    yield put(clearWriteFailed({ error: err instanceof Error ? err.message : 'unknown' }));
    if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
  }
}

/** `hydrateRequested` -> GET the per-cell frontier and load it (server-authoritative). */
export function* hydrateProgress(
  action: ReturnType<typeof hydrateRequested>,
): Generator<unknown, void, unknown> {
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const { exerciseId } = action.payload;
  try {
    const cells = (yield getExerciseProgress(exerciseId)) as ScaleProgressCell[];
    yield put(progressHydrated({ exerciseId, cells }));
  } catch (err) {
    logger.error('[exercise-progress] hydrate failed', err);
    if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
  }
}

/**
 * `sessionCompleted` -> navigation seam. The slice already records the summary; the
 * Phase 3 screen shell owns the actual route change. Left as an explicit worker so
 * navigation slots in here without re-plumbing the subscription.
 */
export function* onSessionCompleted(): Generator<unknown, void, unknown> {
  // The slice already parked the summary; a Phase 3 navigation handler reads it here
  // to decide where to send the user. For now we only surface it.
  const summary = (yield select(selectLastCompletedSummary)) as SessionSummary | null;
  logger.debug(
    '[exercise-progress] session completed; navigation is the Phase 3 seam',
    summary?.playthroughs,
  );

  // MUS-459: close the backend sitting so it persists its telemetry aggregate and emits
  // tier-session-finished. Best-effort and flag-guarded: if the open degraded there is
  // no id to close, and a failed close never blocks the (navigation) seam.
  if (!isFeatureEnabled(FeatureFlag.USE_EXERCISES)) return;

  const playSessionId = (yield select(selectPlaySessionId)) as string | null;
  if (playSessionId == null) return;

  const { sessionId } = (yield select(
    (state: WithEventReducer) => state.eventReducer,
  )) as WithEventReducer['eventReducer'];

  try {
    yield closePlaySession(playSessionId, {
      analytics_session_id: sessionId,
      datetime: new Date().toISOString(),
      platform: getPlatformInfo(),
    });
  } catch (err) {
    logger.error('[exercise-progress] play-session close failed', err);
    if (isServerErrorOrNonAxios(err)) awsRum?.recordError(err);
  }
}

export default [
  takeEvery(sessionStarted.type, openExercisePlaySession),
  takeEvery(pointEarned.type, relayEarnedClear),
  takeEvery(playthroughCompleted.type, postPlaythroughEvent),
  takeEvery(hydrateRequested.type, hydrateProgress),
  takeEvery(sessionCompleted.type, onSessionCompleted),
];
